/**
 * Threat-intelligence feeds and indicators.
 *
 * The indicator table shows **effective** confidence — after decay — beside the stated value,
 * because the difference is the whole point. An indicator reported at High six weeks ago is not
 * High today, and a console that shows only what the feed claimed teaches its operators to trust
 * stale intelligence.
 */

import { useState } from 'react'
import { api, type Feed, type Indicator, type Suppression } from '../api'
import { useSession } from '../App'
import {
  Async,
  Card,
  Chip,
  Empty,
  Modal,
  Mono,
  Note,
  fmtRelative,
  useAsync,
  useToast,
} from '../components/ui'

export default function Intel() {
  const { me } = useSession()
  const [tab, setTab] = useState<'feeds' | 'indicators' | 'suppressed'>('feeds')
  const feeds = useAsync<Feed[]>(() => api.get<Feed[]>('/intel/feeds'), [me.tenant])
  const indicators = useAsync<Indicator[]>(() => api.get<Indicator[]>('/intel/indicators?limit=500'), [me.tenant])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Threat intelligence</h1>
          <div className="sub">
            Feeds selected by what the requirements ask for, not by what is available. Indicators
            decay to nothing across an explicit validity window, so stale intelligence degrades
            into low-priority signals rather than alerting at full severity until it vanishes.
          </div>
        </div>
        <div className="actions">
          <button className={tab === 'feeds' ? 'primary' : ''} onClick={() => setTab('feeds')}>
            Feeds
          </button>
          <button className={tab === 'indicators' ? 'primary' : ''} onClick={() => setTab('indicators')}>
            Indicators
          </button>
          <button className={tab === 'suppressed' ? 'primary' : ''} onClick={() => setTab('suppressed')}>
            Suppressed
          </button>
        </div>
      </div>

      {tab === 'feeds' ? (
        <Feeds feeds={feeds} />
      ) : tab === 'indicators' ? (
        <Indicators indicators={indicators} onChanged={() => indicators.reload()} />
      ) : (
        <Suppressed />
      )}
    </>
  )
}

function Feeds({ feeds }: { feeds: ReturnType<typeof useAsync<Feed[]>> }) {
  const { can } = useSession()
  const [editing, setEditing] = useState<Feed | null>(null)

  return (
    <>
      <Async state={feeds} empty={<Card><Empty title="No feeds registered" /></Card>}>
        {(list) => (
          <div className="grid cols-2">
            {list.map((f) => (
              <Card
                key={f.id}
                title={
                  <span className="row" style={{ gap: 8 }}>
                    {f.name}
                    <Chip
                      tone={
                        f.status === 'ok'
                          ? 'ok'
                          : f.status === 'scaffold'
                            ? 'neutral'
                            : f.status === 'error'
                              ? 'danger'
                              : 'warn'
                      }
                    >
                      {f.status.replace(/_/g, ' ')}
                    </Chip>
                  </span>
                }
                subtitle={`${f.provider} · ${f.kind}`}
                actions={
                  can('configure_feeds') && !f.scaffold ? (
                    <span className="row" style={{ gap: 6 }}>
                      <PollNow feed={f} onDone={feeds.reload} />
                      <button className="sm" onClick={() => setEditing(f)}>
                        Configure
                      </button>
                    </span>
                  ) : null
                }
              >
                <p className="small muted">{f.description}</p>

                {f.scaffold && (
                  <Note>
                    The adapter is registered but the integration is not implemented. It is shown as
                    a scaffold rather than as a working feed that mysteriously returns nothing —
                    which is how an operator concludes there is no threat when there is no
                    integration.
                  </Note>
                )}

                {f.warnings.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Note tone="warn">
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {f.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </Note>
                  </div>
                )}

                <div className="row wrap" style={{ marginTop: 10 }}>
                  {f.supplies.map((s) => (
                    <Chip key={s} tone="outline">
                      {s}
                    </Chip>
                  ))}
                  {!f.requires_key && <Chip tone="ok">no key needed</Chip>}
                  {f.indicator_count > 0 && <Chip tone="neutral">{f.indicator_count} indicators</Chip>}
                </div>

                <LastPoll feed={f} />

                {f.docs_url && (
                  <div className="small" style={{ marginTop: 8 }}>
                    <a href={f.docs_url} target="_blank" rel="noreferrer noopener">
                      Provider documentation ↗
                    </a>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </Async>

      {editing && <FeedConfig feed={editing} onClose={() => setEditing(null)} onSaved={feeds.reload} />}
    </>
  )
}

/**
 * What the last poll did.
 *
 * Never polled and polled-with-nothing-to-report are shown as different things on purpose. They
 * are the same number of indicators and opposite facts, and presenting them alike is how a feed
 * that has never once worked reads as a quiet week.
 */
function LastPoll({ feed }: { feed: Feed }) {
  if (feed.scaffold) return null
  const last = feed.last_fetch
  if (!last) {
    return (
      <div className="small muted" style={{ marginTop: 8 }}>
        {feed.status === 'ok'
          ? 'Never polled by this process. Configure an indicator URL, or use Poll now.'
          : 'Not polled.'}
      </div>
    )
  }
  const when = new Date(last.at).toLocaleString()
  if (last.error) {
    return (
      <div style={{ marginTop: 8 }}>
        <Note tone="warn">
          Last poll {when} failed: {last.error} The indicators already held are unchanged.
        </Note>
      </div>
    )
  }
  return (
    <div className="small muted" style={{ marginTop: 8 }}>
      Last poll {when}: {last.accepted} accepted
      {last.skipped > 0 ? `, ${last.skipped} skipped` : ''} of {last.seen} offered.
    </div>
  )
}

/** Poll one feed now: the real fetch, with the real credential rules, reported as it happened. */
function PollNow({ feed, onDone }: { feed: Feed; onDone: () => void }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  if (feed.status !== 'ok') return null
  return (
    <button
      className="sm"
      disabled={busy}
      onClick={() => {
        setBusy(true)
        api
          .post<{ ok: boolean; summary: string }>(`/intel/feeds/${feed.id}/fetch`, {})
          .then((r) => toast(r.ok ? 'ok' : 'error', r.summary))
          .catch((e: Error) => toast('error', e.message))
          .finally(() => {
            setBusy(false)
            onDone()
          })
      }}
    >
      {busy ? 'Polling…' : 'Poll now'}
    </button>
  )
}

function FeedConfig({ feed, onClose, onSaved }: { feed: Feed; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [values, setValues] = useState<Record<string, string>>({})
  const [enabled, setEnabled] = useState(true)
  const [ttl, setTtl] = useState(30)
  const [busy, setBusy] = useState(false)

  const endpointOverridden = ['base_url', 'url', 'host', 'instance', 'tenant_id'].some((k) => values[k]?.trim())

  function save() {
    setBusy(true)
    api
      .put<{ warnings: string[] }>(`/intel/feeds/${feed.id}`, {
        values,
        enabled,
        default_ttl_days: ttl,
      })
      .then((r) => {
        toast(r.warnings.length ? 'error' : 'ok', r.warnings.length ? r.warnings.join(' ') : 'Feed configured')
        onSaved()
        if (!r.warnings.length) onClose()
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      title={`Configure ${feed.name}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <p className="small muted">{feed.description}</p>

      {feed.fields.map((f) => (
        <div className="field" key={f.name}>
          <label htmlFor={`f-${f.name}`}>
            {f.label}
            {f.secret && <Chip tone="outline" >secret</Chip>}
          </label>
          <input
            id={`f-${f.name}`}
            type={f.secret ? 'password' : 'text'}
            autoComplete="off"
            value={values[f.name] ?? ''}
            onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
          />
          {f.help && <div className="hint">{f.help}</div>}
        </div>
      ))}

      {endpointOverridden && (
        <Note tone="warn">
          You have pointed this feed at a custom endpoint. The platform's own configured
          credentials will <strong>not</strong> be sent there — you must supply the credential
          alongside the endpoint. Without that rule, redirecting a feed would be a way to make the
          platform post its own API key to a host you chose.
        </Note>
      )}

      <div className="grid cols-2" style={{ marginTop: 12 }}>
        <div className="field">
          <label htmlFor="ttl">Default validity (days)</label>
          <input id="ttl" type="number" min={1} value={ttl} onChange={(e) => setTtl(Number(e.target.value))} />
          <div className="hint">
            How long an indicator from this feed is believed when it publishes no expiry of its
            own. Confidence decays linearly to zero across this window.
          </div>
        </div>
        <div className="field">
          <label>Enabled</label>
          <label className="row" style={{ gap: 7, marginTop: 6 }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="small muted">Fetch from this source</span>
          </label>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Values this workspace has decided to disbelieve.
 *
 * Every one shows what it has hidden and when it lapses, because a suppression whose effect nobody
 * can see is indistinguishable from intelligence that never arrived — and one that lapses silently
 * brings the alerts back with no explanation, which reads as new activity.
 */
function Suppressed() {
  const { can, me } = useSession()
  const toast = useToast()
  const list = useAsync<Suppression[]>(
    () => api.get<Suppression[]>('/intel/suppressions'),
    [me.tenant],
  )

  return (
    <Card
      title="Suppressed indicators"
      subtitle="Every one expires — there is deliberately no permanent option"
      tight
    >
      <Async
        state={list}
        empty={
          <Empty title="Nothing is suppressed">
            When intelligence is wrong about a value — your own egress proxy, a CDN address —
            suppress it from the indicator list with a reason and a term.
          </Empty>
        }
      >
        {(rows) => (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Value</th>
                  <th>Kind</th>
                  <th>Reason</th>
                  <th>Hidden</th>
                  <th>Expires</th>
                  <th>By</th>
                  {can('write_incidents') && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={`${s.kind}:${s.value}`}>
                    <td><Mono copy>{s.value}</Mono></td>
                    <td className="small"><Chip tone="outline">{s.kind}</Chip></td>
                    <td className="small">{s.reason}</td>
                    <td className="small">
                      {s.suppressed_count > 0 ? (
                        <Chip tone={s.suppressed_count > 100 ? 'warn' : 'neutral'}>
                          {s.suppressed_count} match{s.suppressed_count === 1 ? '' : 'es'}
                        </Chip>
                      ) : (
                        <span className="faint">none yet</span>
                      )}
                    </td>
                    <td className="small">
                      <Chip tone={s.expiring_soon ? 'warn' : 'neutral'}>
                        {s.days_left} day{s.days_left === 1 ? '' : 's'}
                      </Chip>
                    </td>
                    <td className="small muted">{s.created_by}</td>
                    {can('write_incidents') && (
                      <td>
                        <button
                          className="sm"
                          onClick={() => {
                            api
                              .del(`/intel/suppressions/${s.kind}/${encodeURIComponent(s.value)}`)
                              .then(() => {
                                toast('ok', `${s.value} is believed again.`)
                                list.reload()
                              })
                              .catch((e: Error) => toast('error', e.message))
                          }}
                        >
                          Believe again
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Async>
    </Card>
  )
}

/** Suppress one indicator: a reason and a term, both required. */
function SuppressButton({ indicator, onDone }: { indicator: Indicator; onDone: () => void }) {
  const { can } = useSession()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [days, setDays] = useState(30)
  const [busy, setBusy] = useState(false)
  if (!can('write_incidents')) return null

  return (
    <>
      <button className="sm" onClick={() => setOpen(true)} title="Stop believing this value">
        Suppress
      </button>
      {open && (
        <Modal
          title={`Suppress ${indicator.value}`}
          onClose={() => setOpen(false)}
          footer={
            <>
              <button onClick={() => setOpen(false)}>Cancel</button>
              <button
                className="primary"
                disabled={busy || reason.trim().length < 10}
                onClick={() => {
                  setBusy(true)
                  api
                    .post('/intel/suppressions', {
                      kind: indicator.kind,
                      value: indicator.value,
                      reason,
                      days,
                    })
                    .then(() => {
                      toast('ok', `${indicator.value} suppressed for ${days} days.`)
                      setOpen(false)
                      onDone()
                    })
                    .catch((e: Error) => toast('error', e.message))
                    .finally(() => setBusy(false))
                }}
              >
                {busy ? 'Suppressing…' : 'Suppress'}
              </button>
            </>
          }
        >
          <Note>
            This stops matching on the value, not on the indicator: feeds are polled on a schedule,
            so deleting the indicator would bring it back within the hour. Detection using this
            value stops until the suppression expires.
          </Note>
          <div className="field">
            <label htmlFor="sup-reason">Reason</label>
            <textarea
              id="sup-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What this is, and how you know. e.g. Confirmed as our own egress proxy, ticket OPS-4471."
            />
            <div className="faint small">
              Somebody reviewing this in a month has to be able to act on it.
            </div>
          </div>
          <div className="field">
            <label htmlFor="sup-days">Expires after</label>
            <select id="sup-days" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[7, 14, 30, 60, 90].map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
            </select>
            <div className="faint small">
              There is no permanent option. A suppression nobody revisits is how a detection gap
              becomes invisible.
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

function Indicators({
  indicators,
  onChanged,
}: {
  indicators: ReturnType<typeof useAsync<Indicator[]>>
  onChanged: () => void
}) {
  const { can } = useSession()
  const toast = useToast()
  const [q, setQ] = useState('')
  const [adding, setAdding] = useState(false)
  const [lines, setLines] = useState('')
  const [busy, setBusy] = useState(false)

  function add() {
    setBusy(true)
    api
      .post<{ added: number; skipped: string[] }>('/intel/indicators', { lines: lines.split('\n') })
      .then((r) => {
        toast(r.skipped.length ? 'error' : 'ok', `${r.added} added${r.skipped.length ? `, ${r.skipped.length} skipped` : ''}`)
        if (r.skipped.length) toast('error', r.skipped[0] ?? '')
        setLines('')
        setAdding(false)
        indicators.reload()
      })
      .catch((e: Error) => toast('error', e.message))
      .finally(() => setBusy(false))
  }

  return (
    <>
      <Card
        title="Indicators"
        subtitle="Effective confidence is after decay — that is the number detection actually uses"
        actions={
          <>
            <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 200 }} aria-label="Search indicators" />
            {can('configure_feeds') && (
              <button className="sm" onClick={() => setAdding(true)}>
                Add indicators
              </button>
            )}
          </>
        }
        tight
      >
        <Async
          state={indicators}
          empty={
            <Empty title="No indicators">
              Configure a feed, or paste indicators in directly.
            </Empty>
          }
        >
          {(list) => {
            const rows = list.filter(
              (i) => !q || `${i.value} ${i.category} ${i.source}`.toLowerCase().includes(q.toLowerCase()),
            )
            return (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Indicator</th>
                      <th>Kind</th>
                      <th>Category</th>
                      <th>Stated</th>
                      <th>Effective</th>
                      <th>Sources</th>
                      <th>Expires</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((i) => {
                      const sources = i.source.split(',').filter(Boolean)
                      return (
                        <tr key={`${i.kind}:${i.value}`}>
                          <td>
                            <Mono copy>{i.value}</Mono>
                            {i.description && <div className="faint small">{i.description}</div>}
                          </td>
                          <td className="small"><Chip tone="outline">{i.kind}</Chip></td>
                          <td className="small">{i.category}</td>
                          <td className="small muted">{i.confidence}</td>
                          <td>
                            <Chip
                              tone={
                                i.effective_confidence >= 70
                                  ? 'critical'
                                  : i.effective_confidence >= 45
                                    ? 'high'
                                    : i.effective_confidence > 0
                                      ? 'medium'
                                      : 'neutral'
                              }
                              title="After time decay — this is what a match would score"
                            >
                              {i.effective_confidence}
                            </Chip>
                          </td>
                          <td className="small">
                            {sources.map((s) => (
                              <Chip key={s} tone={sources.length > 1 ? 'ok' : 'outline'}>
                                {s}
                              </Chip>
                            ))}
                            {sources.length > 1 && <div className="faint small">corroborated</div>}
                          </td>
                          <td className="small muted nowrap">
                            {i.expired ? <Chip tone="neutral">expired</Chip> : fmtRelative(i.valid_until)}
                          </td>
                          <td>
                            <SuppressButton indicator={i} onDone={onChanged} />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          }}
        </Async>
      </Card>

      {adding && (
        <Modal
          title="Add indicators"
          onClose={() => setAdding(false)}
          footer={
            <>
              <button onClick={() => setAdding(false)}>Cancel</button>
              <button className="primary" onClick={add} disabled={busy || !lines.trim()}>
                {busy ? 'Adding…' : 'Add'}
              </button>
            </>
          }
        >
          <p className="small muted">
            One per line. The kind is inferred from the value where possible; where it cannot be, the
            line is skipped rather than guessed — an indicator stored under the wrong kind silently
            matches nothing. Add it explicitly as <Mono>value,kind</Mono> or{' '}
            <Mono>value,kind,category</Mono>.
          </p>
          <textarea
            rows={10}
            placeholder={'203.0.113.77\nevil-c2.example.com,domain,c2\nd41d8cd98f00b204e9800998ecf8427e,file_hash,malware'}
            value={lines}
            onChange={(e) => setLines(e.target.value)}
          />
        </Modal>
      )}
    </>
  )
}
